import { MultipleQueriesQuery } from "@algolia/client-search";
import { InputAdornment } from "@mui/material";
import FormHelperText from "@mui/material/FormHelperText";
import Paper from "@mui/material/Paper";
import {
  StyledEngineProvider,
  Theme,
  ThemeProvider,
} from "@mui/material/styles";
import TextField, { TextFieldProps } from "@mui/material/TextField";
import { CSSProperties } from "@mui/styles";
import algoliasearch from "algoliasearch/lite";
import { useEnvContext } from "context/EnvContext";
import {
  ENV_DEMO,
  ENV_DEVELOPMENT,
  ENV_PREPROD,
  ENV_STAGING,
} from "core/model/config";
import { concatStrings } from "core/model/utils/strings";
import { AlgoliaAnalyticsName, AppType, CSSPosition, Env } from "core/types";
import {
  BORDER_BOTTOM_DARK,
  CUSTOM_BLACK,
  ERROR_COLOR,
  WHITE,
} from "ds_legacy/materials/colors";
import {
  SEARCH_BAR_WIDTH_MEDIUM,
  SEARCH_BAR_WIDTH_SMALL,
  border,
  padding,
} from "ds_legacy/materials/metrics";
import { getTheme } from "ds_legacy/materials/theme";
import {
  Caption,
  FONT_SIZE_14,
  FONT_SIZE_16,
} from "ds_legacy/materials/typography";
import { useOnClickAlgoliaItem } from "dsl/atoms/AlgoliaSelect/search-insights";
import { useBrowserContext } from "dsl/atoms/BrowserProvider";
import { MenuItem } from "dsl/atoms/MenuItem";
import { useGetAnalyticsTags } from "dsl/hooks";
import { ComponentType, ReactNode, useMemo, useState } from "react";
import AutoSuggest, {
  RenderSuggestionParams,
  RenderSuggestionsContainerParams,
} from "react-autosuggest";
import { AutocompleteProvided } from "react-instantsearch-core";
import {
  Configure,
  InstantSearch,
  connectAutoComplete,
} from "react-instantsearch-dom";
import { useCountry } from "reduxentities/hooks";
import styled from "styled-components";
import { useTranslations } from "translations";
import Translations from "translations/types";
import { useMedia } from "../ResponsiveMedia";

declare module "@mui/styles/defaultTheme" {
  // eslint-disable-next-line @typescript-eslint/consistent-type-definitions, @typescript-eslint/no-empty-object-type
  interface DefaultTheme extends Theme {}
}
export type CustomRenderSuggestionParams = RenderSuggestionParams & {
  isEmpty: boolean;
  isLast: boolean;
};

const TextFieldWithBorder = styled(TextField)<{
  height?: CSSProperties["height"];
  error?: boolean;
}>`
  & .MuiOutlinedInput-root {
    & fieldset {
      border: ${({ error }) =>
        border({ color: error ? ERROR_COLOR : BORDER_BOTTOM_DARK })};
    }
    & input::placeholder {
      opacity: 0.9;
    }
    height: ${({ height }) => height};
  }
  & .MuiFormHelperText-root {
    color: ${({ error }) => (error ? ERROR_COLOR : "inherit")};
  }
`;

function RenderInput({
  app,
  error,
  hasAutoFocus,
  height,
  helperText,
  isDark,
  label,
  margin = "dense",
  placeholder,
  startAdornment,
  value,
  variant = "standard",
  width,
  ...inputProps
}: {
  app: AppType;
  error: string;
  hasAutoFocus: boolean;
  height: CSSProperties["height"];
  helperText: ReactNode;
  isDark: boolean;
  label: string;
  margin: TextFieldProps["margin"];
  placeholder: string;
  startAdornment?: React.JSX.Element;
  value: any;
  variant: TextFieldProps["variant"];
  width: string;
}) {
  const hasError = error !== null && error !== undefined && error !== "";
  return (
    <StyledEngineProvider injectFirst>
      <ThemeProvider theme={getTheme(app, isDark)}>
        <TextFieldWithBorder
          autoFocus={hasAutoFocus}
          error={hasError}
          height={height}
          helperText={helperText}
          inputProps={inputProps}
          InputProps={{
            startAdornment: startAdornment ? (
              <InputAdornment aria-hidden position="start">
                {startAdornment}
              </InputAdornment>
            ) : null,
          }}
          label={label}
          margin={margin}
          placeholder={placeholder}
          sx={{
            "& .MuiFormLabel-root": {
              fontSize: FONT_SIZE_14,
            },
            width,
          }}
          value={value}
          variant={variant}
        />
      </ThemeProvider>
    </StyledEngineProvider>
  );
}

export const defaultRenderSuggestionsContainer = (
  style?: Record<string, any>,
) =>
  function defaultRenderSuggestions({
    children,
    containerProps,
    position,
  }: {
    children: ReactNode;
    containerProps: Record<string, any>;
    position?: CSSPosition;
  }) {
    const { key, ...restContainerProps } = containerProps;
    return (
      <Paper
        key={key}
        {...restContainerProps}
        sx={{
          maxHeight: "450px",
          overflow: "auto",
          overflowX: "hidden",
          position: position || "absolute",
          zIndex: 1,
          ...style,
        }}
      >
        {children}
      </Paper>
    );
  };

const defaultRenderSuggestion = (width: any) =>
  function renderSuggestion(
    suggestion: Record<string, any>,
    { isHighlighted }: { isHighlighted: boolean },
  ) {
    return (
      <MenuItem
        component="div"
        data-testid="suggestion-item"
        selected={isHighlighted}
        sx={{
          cursor: "pointer",
          fontSize: FONT_SIZE_14,
          padding: padding(1, 2),
          width,
        }}
      >
        {suggestion.name}
      </MenuItem>
    );
  };

const AutoComplete = connectAutoComplete(
  ({
    ariaLabel,
    currentRefinement,
    CustomNoResult,
    customNoResultMessage,
    env,
    error,
    hasAutoClear,
    height,
    hits,
    indexName,
    isDark = false,
    isHidden,
    label,
    margin,
    onBlur,
    onChange,
    onFocus,
    placeholder,
    refine,
    renderSuggestion,
    renderSuggestionsContainer,
    shouldAlwaysDisplayPlaceholder,
    shouldClearOnSelect,
    shouldUseCustomNoResult,
    startAdornment,
    testid,
    translations,
    value,
    variant,
    width,
    withIndexEnv,
  }: AutocompleteProvided & {
    CustomNoResult?: ComponentType<any>;
    ariaLabel?: string;
    currentRefinement: string;
    customNoResultMessage?: string;
    env: Env;
    error?: string;
    hasAutoClear?: boolean;
    height?: CSSProperties["height"];
    hits: Array<number>;
    indexName: string;
    isDark: boolean;
    isHidden?: boolean;
    label?: string;
    margin?: TextFieldProps["margin"];
    onBlur?: () => void;
    onChange: (arg: any) => void;
    onFocus?: () => void;
    placeholder: string;
    refine: (v: any) => any;
    renderSuggestion?: (
      suggestion: any,
      params: CustomRenderSuggestionParams,
    ) => ReactNode;
    renderSuggestionsContainer?: (
      params: RenderSuggestionsContainerParams,
    ) => ReactNode;
    shouldAlwaysDisplayPlaceholder?: boolean;
    shouldClearOnSelect?: boolean;
    shouldUseCustomNoResult?: boolean;
    startAdornment?: React.JSX.Element;
    translations: Translations;
    withIndexEnv?: boolean;
    value: any;
    variant?: TextFieldProps["variant"];
    width: number | string;
    testid?: string;
  }) => {
    const { onClickAlgoliaItem } = useOnClickAlgoliaItem();
    const [inputValue, setInputValue] = useState<string | null>(null);
    const valueName = value?.name || "";
    const displayInputValue = inputValue != null ? inputValue : valueName;
    const { isChrome } = useBrowserContext();
    const { isMobile } = useMedia();
    const { app } = useEnvContext();

    const hasNoResults =
      displayInputValue.length && !hits.length && currentRefinement !== "";

    const hasExactHit = hits.some(
      (hit) => (hit.name ?? "").trim() === currentRefinement.trim(),
    );

    const showCustomNoResult =
      currentRefinement.trim() !== "" &&
      Boolean(CustomNoResult) &&
      !hasExactHit &&
      !value?.id;

    const customNoResultElement =
      showCustomNoResult && CustomNoResult != null ? (
        <CustomNoResult currentRefinement={currentRefinement} />
      ) : null;

    const helperText =
      error ||
      (shouldUseCustomNoResult &&
        currentRefinement.trim() !== "" &&
        !hasExactHit &&
        customNoResultMessage) ||
      (hasNoResults && !showCustomNoResult && translations.general.noResult) ||
      "";

    const inputProps = {
      "aria-label": ariaLabel,
      autoComplete: isChrome ? `new-${indexName || "input"}` : "off",
      error,
      height,
      helperText,
      id: testid ?? indexName,
      isDark,
      label,
      margin,
      onBlur,
      onChange: (e: any, { newValue }: { newValue: any }) => {
        if (newValue === "") {
          onChange(null);
          refine("");
          setInputValue(newValue);
        } else if (typeof newValue === "string") {
          setInputValue(valueName);
        } else {
          setInputValue(newValue.name);
        }
      },
      onFocus,
      placeholder,
      startAdornment,
      value: currentRefinement || displayInputValue,
      variant,
      width,
    };

    const renderInputWithApp = (autosuggestInputProps: any) => {
      const { key, ...restInputProps } = autosuggestInputProps;
      return <RenderInput key={key} {...restInputProps} app={app} />;
    };

    return (
      <div>
        {shouldAlwaysDisplayPlaceholder && (
          <Caption margin="0px">
            {inputValue && placeholder}
            &nbsp;
          </Caption>
        )}
        <AutoSuggest<any, any>
          focusInputOnSuggestionClick={false}
          getSuggestionValue={(v) => v}
          inputProps={inputProps}
          renderInputComponent={renderInputWithApp}
          renderSuggestion={(suggestion, params) => {
            const isEmpty = hits.length === 0;
            const isLast =
              hits.length > 0 &&
              suggestion.objectID === hits[hits.length - 1].objectID;
            const customParams = { ...params, isEmpty, isLast };

            if (renderSuggestion) {
              return renderSuggestion?.(suggestion, customParams);
            }
            return defaultRenderSuggestion(width)(suggestion, customParams);
          }}
          renderSuggestionsContainer={
            renderSuggestionsContainer || defaultRenderSuggestionsContainer()
          }
          suggestions={
            shouldUseCustomNoResult &&
            !customNoResultMessage &&
            !CustomNoResult &&
            hits?.length === 0
              ? [{ isEmpty: true }]
              : hits
          }
          theme={{
            container: {
              display: isHidden ? "none" : undefined,
            },
            input: {
              color: isDark ? WHITE : CUSTOM_BLACK,
              fontSize: isMobile ? FONT_SIZE_16 : FONT_SIZE_14,
            },
            suggestion: {
              margin: "0",
              padding: "0",
            },
            suggestionsList: {
              listStyle: "none",
              margin: "0",
              padding: "0",
            },
          }}
          onSuggestionsClearRequested={() => {
            if (hasAutoClear) {
              refine("");
            }
          }}
          onSuggestionSelected={(e, { suggestion }) => {
            onChange(suggestion);

            onClickAlgoliaItem({
              index: getIndex(indexName, env, withIndexEnv),
              objectIDs: [suggestion.objectID],
              positions: [suggestion.__position],
              queryID: suggestion.__queryID,
            });

            if (shouldClearOnSelect) {
              refine("");
              setInputValue("");
            } else {
              const suggestionValue = suggestion?.name || "";
              refine(suggestionValue);
            }
          }}
          onSuggestionsFetchRequested={(request) => {
            refine(request.value);
          }}
        />
        {customNoResultElement ? (
          <FormHelperText component="div">
            {customNoResultElement}
          </FormHelperText>
        ) : null}
      </div>
    );
  },
);

export function getIndex(
  name: string,
  env: string,
  withEnv: boolean | null | undefined,
): string {
  if (!withEnv) return name;

  const envMap: { [key: string]: string } = {
    [ENV_DEMO]: ENV_PREPROD,
    [ENV_DEVELOPMENT]: ENV_STAGING,
  };

  return concatStrings(name, (envMap[env] || env).toUpperCase(), "_");
}

export const useGetAlgoliaApiKey = () => {
  const { app, config } = useEnvContext();
  const country = useCountry();

  const appId = config?.algolia.appId ?? "";
  const apiKey = config?.algolia.apiKey ?? "";

  if (!apiKey) {
    console.error(`missing algolia key for app: ${app} country: ${country}`);
  }
  if (!appId) {
    console.error(`missing algolia app id for country: ${country}`);
  }

  return { apiKey, appId };
};

export default function AlgoliaSearchBar({
  algoliaAnalyticsName,
  ariaLabel,
  aroundLatLng,
  aroundRadius,
  attributesToRetrieve,
  className,
  CustomNoResult,
  customNoResultMessage,
  error,
  filters,
  hasAutoClear,
  height,
  hitsPerPage,
  indexName,
  isDark = false,
  isFullSize = false,
  isHidden,
  isSmall = false,
  label,
  margin,
  onBlur,
  onChange,
  onFocus,
  placeholder,
  renderSuggestion,
  renderSuggestionsContainer,
  restrictSearchableAttributes,
  shouldAlwaysDisplayPlaceholder,
  shouldClearOnSelect,
  shouldUseCustomNoResult,
  startAdornment,
  testid,
  value,
  variant,
  withIndexEnv,
}: {
  CustomNoResult?: ComponentType<any>;
  algoliaAnalyticsName: AlgoliaAnalyticsName;
  ariaLabel?: string;
  aroundLatLng?: string;
  aroundRadius?: number;
  attributesToRetrieve?: string[];
  className?: string;
  customNoResultMessage?: string;
  error?: string;
  filters?: string;
  hasAutoClear?: boolean;
  height?: CSSProperties["height"];
  hitsPerPage?: number;
  indexName: string;
  isDark?: boolean;
  isFullSize?: boolean;
  isHidden?: boolean;
  isSmall?: boolean;
  label?: string;
  margin?: TextFieldProps["margin"];
  onBlur?: () => void;
  onChange: (arg: any) => void;
  onFocus?: () => void;
  placeholder: string;
  renderSuggestion?: (
    suggestion: any,
    params: CustomRenderSuggestionParams,
  ) => ReactNode;
  renderSuggestionsContainer?: (
    params: RenderSuggestionsContainerParams,
  ) => ReactNode;
  restrictSearchableAttributes?: string[];
  shouldAlwaysDisplayPlaceholder?: boolean;
  shouldClearOnSelect?: boolean;
  shouldUseCustomNoResult?: boolean;
  startAdornment?: React.JSX.Element;
  withIndexEnv?: boolean;
  value?: any;
  variant?: TextFieldProps["variant"];
  testid?: string;
}) {
  const { env } = useEnvContext();
  const { apiKey, appId } = useGetAlgoliaApiKey();
  const analyticsTags = useGetAnalyticsTags({ algoliaAnalyticsName });
  const translations = useTranslations();

  const searchClient = useMemo(() => {
    const algoliaClient = algoliasearch(appId, apiKey);
    return {
      ...algoliaClient,
      // disables empty searches = empty queries / initial focus won't trigger results by default
      search(requests: MultipleQueriesQuery[]) {
        if (requests.every(({ params }) => !params?.query)) {
          return Promise.resolve({
            results: requests.map(() => ({
              exhaustiveNbHits: false,
              hits: [],
              hitsPerPage: 0,
              nbHits: 0,
              nbPages: 0,
              page: 0,
              params: "",
              processingTimeMS: 0,
              query: "",
            })),
          });
        }

        return algoliaClient.search(
          requests as Parameters<typeof algoliaClient.search>[0],
        );
      },
    };
  }, [appId, apiKey]);

  let width;
  if (isFullSize) width = "100%";
  else if (isSmall) width = SEARCH_BAR_WIDTH_SMALL;
  else width = SEARCH_BAR_WIDTH_MEDIUM;

  return (
    <div className={className} style={{ height, width }}>
      <InstantSearch
        indexName={getIndex(indexName, env, withIndexEnv)}
        searchClient={searchClient}
      >
        <Configure
          analyticsTags={analyticsTags}
          aroundLatLng={aroundLatLng}
          aroundRadius={aroundRadius}
          attributesToRetrieve={attributesToRetrieve}
          clickAnalytics
          filters={filters}
          hitsPerPage={hitsPerPage || 5}
          restrictSearchableAttributes={restrictSearchableAttributes}
        />
        <AutoComplete
          ariaLabel={ariaLabel}
          CustomNoResult={CustomNoResult}
          customNoResultMessage={customNoResultMessage}
          env={env}
          error={error}
          hasAutoClear={hasAutoClear}
          height={height}
          indexName={indexName}
          isDark={isDark}
          isHidden={isHidden}
          label={label}
          margin={margin}
          placeholder={placeholder}
          renderSuggestion={renderSuggestion}
          renderSuggestionsContainer={renderSuggestionsContainer}
          shouldAlwaysDisplayPlaceholder={shouldAlwaysDisplayPlaceholder}
          shouldClearOnSelect={shouldClearOnSelect}
          shouldUseCustomNoResult={shouldUseCustomNoResult}
          startAdornment={startAdornment}
          testid={testid}
          translations={translations}
          value={value}
          variant={variant}
          width={width}
          withIndexEnv={withIndexEnv}
          onBlur={onBlur}
          onChange={onChange}
          onFocus={onFocus}
        />
      </InstantSearch>
    </div>
  );
}
